import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { DContext } from '../../context/DataContext'
import { FiArrowRight, FiMapPin, FiSearch } from 'react-icons/fi'

function SearchBlock({ showButton, onSuggestionSelected }) {

    const {
        packages,
        destinations,
        packageTitleSuggestions,
        searchPlaceholders,
        filterPackages,
        renderDropdown,
        setRenderDropdown,
        enteredDestination,
        setEnteredDestination,
        enteredActivity,
        setEnteredActivity,
        enteredText,
        setEnteredText
    } = useContext(DContext)

    const { t } = useTranslation()
    const navPage = useNavigate()


    const [searchDestination, setSearchDestination] = useState(null)
    const [searchActivity, setSearchActivity] = useState(null)
    const [searchPackage, setSearchPackage] = useState(null)

    const plannerContent = t('homepage.planner', { returnObjects: true })

    const experienceOptions = [
        'boat',
        'snorkeling',
        'beach',
        'walk',
        'sunset',
        'island',
        'adventure',
        'riding',
        'swim',
        'sunbathe',
        'hike',
        'pool',
        'waterfall',
        'wildlife',
        'viking',
        'cooking',
        'jungle',
        'nature',
        'temple'
    ]

    const destinationPlaceholder = searchPlaceholders?.destination || plannerContent?.placeholders?.destination
    const activityPlaceholder = searchPlaceholders?.activity || plannerContent?.placeholders?.activity
    const packagePlaceholder = searchPlaceholders?.package || plannerContent?.placeholders?.text

    const buildQueryString = () => {
        const params = new URLSearchParams()
        if (enteredDestination && enteredDestination.trim()) {
            params.set('destination', enteredDestination.trim())
        }
        else if (enteredActivity && enteredActivity.trim()) {
            params.set('activity', enteredActivity.trim())
        }
        else if (enteredText && enteredText.trim()) {
            params.set('package', enteredText.trim())
        }

        const query = params.toString()
        return query ? `/packages?${query}` : '/packages'
    }

    const uniqueByLowercase = (items) => {
        return (items || []).reduce((unique, item) => {
            const normalizedItem = String(item || '').trim()
            if (!normalizedItem) {
                return unique
            }

            const lowerItem = normalizedItem.toLowerCase()
            if (!unique.some(existing => existing.toLowerCase() === lowerItem)) {
                unique.push(normalizedItem)
            }
            return unique
        }, [])
    }

    const allDestinations = uniqueByLowercase(
        (destinations || []).concat(
            (packages || []).flatMap((pkg) => (Array.isArray(pkg?.destinations) ? pkg.destinations : []))
        )
    )

    const filterIncludes = (items, value) => {
        const query = String(value || '').trim().toLowerCase()
        if (!query) {
            return uniqueByLowercase(items)
        }
        return uniqueByLowercase(
            (items || []).filter((item) => String(item || '').toLowerCase().includes(query))
        )
    }

    const resetOtherFields = (activeField) => {
        if (activeField !== 'destination') {
            setEnteredDestination('')
            setSearchDestination(null)
        }
        if (activeField !== 'activity') {
            setEnteredActivity('')
            setSearchActivity(null)
        }
        if (activeField !== 'package') {
            setEnteredText('')
            setSearchPackage(null)
        }
    }

    const openDropdownForField = (field) => {
        setRenderDropdown(field)
        if (field === 'destination') {
            setSearchDestination(filterIncludes(allDestinations, enteredDestination))
        }
        if (field === 'activity') {
            setSearchActivity(filterIncludes(experienceOptions, enteredActivity))
        }
        if (field === 'package') {
            setSearchPackage(filterIncludes(packageTitleSuggestions || [], enteredText))
        }
    }

    const fetchPackages = (value, dropdown) => {

        const trimmedValue = value.trim()

        setRenderDropdown(dropdown)
        resetOtherFields(dropdown)

        if (dropdown === 'destination') {
            setEnteredDestination(value)
            setSearchDestination(filterIncludes(allDestinations, trimmedValue))
            if (trimmedValue) {
                filterPackages(trimmedValue, 'destination')
            } else {
                filterPackages(null, 'all')
            }
        }
        else if (dropdown === 'activity') {
            setEnteredActivity(value)
            setSearchActivity(filterIncludes(experienceOptions, trimmedValue))
            if (trimmedValue) {
                filterPackages(trimmedValue, 'activity')
            } else {
                filterPackages(null, 'all')
            }
        }
        else if (dropdown === 'package') {
            setEnteredText(value)
            setSearchPackage(filterIncludes(packageTitleSuggestions || [], trimmedValue))
            if (trimmedValue) {
                filterPackages(trimmedValue, 'package')
            } else {
                filterPackages(null, 'all')
            }
        }

    }

    const handlePackageFilter = (value, type) => {
        setRenderDropdown(null)
        setSearchDestination(null)
        setSearchActivity(null)
        setSearchPackage(null)

        if (typeof document !== 'undefined' && document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur()
        }

        if (type === 'destination') {
            setEnteredDestination(value)
            setEnteredActivity('')
            setEnteredText('')
        }
        else if (type === 'activity') {
            setEnteredActivity(value)
            setEnteredDestination('')
            setEnteredText('')
        }
        else if (type === 'package') {
            setEnteredText(value)
            setEnteredDestination('')
            setEnteredActivity('')
        }

        filterPackages(value, type)

        if (typeof onSuggestionSelected === 'function') {
            onSuggestionSelected({ type, value })
        }
    }

    const renderSuggestionList = (items, type) => {
        if (items !== null && items.length > 0) {
            return items.map((item, i) => (
                <button type='button' className='block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100' key={`${type}-${i}`} onClick={() => handlePackageFilter(item, type)}>{item}</button>
            ))
        }

        return <button type='button' className='block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-400' disabled>{t('searchbox.noResult')}!</button>
    }

    return (
        <div className='relative mx-auto w-full overflow-visible rounded-[2rem] border border-sky-100 bg-gradient-to-br from-sky-50 via-blue-100/70 to-cyan-100/70 p-4 shadow-[0_18px_50px_rgba(2,6,23,0.12)] sm:p-5'>
            <div className='pointer-events-none absolute -left-16 -top-20 h-48 w-48 rounded-full bg-cyan-300/45 blur-3xl' />
            <div className='pointer-events-none absolute -bottom-28 -right-8 h-56 w-56 rounded-full bg-blue-300/40 blur-3xl' />

            <div className='relative mb-4 flex flex-wrap items-end justify-between gap-4'>
                <div>
                    <p className='text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500'>{plannerContent?.eyebrow}</p>
                    <h2 className='mt-1 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl'>{plannerContent?.title}</h2>
                    <p className='mt-2 max-w-2xl text-sm text-slate-600'>{plannerContent?.description}</p>
                </div>
            </div>

            <div className='relative grid grid-cols-1 gap-2 lg:grid-cols-[1fr_1fr_1fr_auto]'>
                <div className='relative'>
                    <label htmlFor='search-destination' className='mb-1.5 block text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>Destination</label>
                    <div className='flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 transition focus-within:border-[#1739bd] focus-within:shadow-[0_0_0_3px_rgba(23,57,189,0.12)]'>
                        <FiMapPin className='text-slate-500' />
                        <input
                            className='w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400'
                            value={enteredDestination}
                            onChange={(e) => fetchPackages(e.target.value, 'destination')}
                            onFocus={() => {
                                resetOtherFields('destination')
                                openDropdownForField('destination')
                            }}
                            placeholder={destinationPlaceholder}
                            type='search'
                            id='search-destination'
                        />
                    </div>
                    {(renderDropdown !== null && renderDropdown === 'destination') && (
                        <div className='absolute left-0 right-0 top-[calc(100%+6px)] z-[1200] max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg'>
                            {renderSuggestionList(searchDestination, 'destination')}
                        </div>
                    )}
                </div>

                <div className='relative'>
                    <label htmlFor='search-activity' className='mb-1.5 block text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>Experience</label>
                    <div className='flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 transition focus-within:border-[#1739bd] focus-within:shadow-[0_0_0_3px_rgba(23,57,189,0.12)]'>
                        <FiMapPin className='text-slate-500' />
                        <input
                            className='w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400'
                            value={enteredActivity}
                            onChange={(e) => fetchPackages(e.target.value, 'activity')}
                            onFocus={() => {
                                resetOtherFields('activity')
                                openDropdownForField('activity')
                            }}
                            placeholder={activityPlaceholder}
                            type='search'
                            id='search-activity'
                        />
                    </div>
                    {(renderDropdown !== null && renderDropdown === 'activity') && (
                        <div className='absolute left-0 right-0 top-[calc(100%+6px)] z-[1200] max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg'>
                            {renderSuggestionList(searchActivity, 'activity')}
                        </div>
                    )}
                </div>

                <div className='relative'>
                    <label htmlFor='search-package' className='mb-1.5 block text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500'>Search package</label>
                    <div className='flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 transition focus-within:border-[#1739bd] focus-within:shadow-[0_0_0_3px_rgba(23,57,189,0.12)]'>
                        <FiSearch className='text-slate-500' />
                        <input
                            className='w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400'
                            value={enteredText}
                            onChange={(e) => fetchPackages(e.target.value, 'package')}
                            onFocus={() => {
                                resetOtherFields('package')
                                openDropdownForField('package')
                            }}
                            placeholder={packagePlaceholder}
                            type='search'
                            id='search-package'
                        />
                    </div>
                    {(renderDropdown !== null && renderDropdown === 'package') && (
                        <div className='absolute left-0 right-0 top-[calc(100%+6px)] z-[1200] max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg'>
                            {renderSuggestionList(searchPackage, 'package')}
                        </div>
                    )}
                </div>

                {(showButton && showButton === true) && (
                    <button
                        className='group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#092FB6] to-[#636DE5] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(9,47,182,0.35)] transition hover:brightness-110 lg:min-w-[170px] sm:text-base'
                        onClick={() => navPage(buildQueryString())}
                    >
                        {plannerContent?.button}
                        <FiArrowRight className='transition-transform duration-300 group-hover:translate-x-1' />
                    </button>
                )}
            </div>
        </div>
    )
}

export default SearchBlock